import { FC, useCallback, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button, Input } from '@features/ui';
import { CloseIcon } from '@features/ui/icons';
import {
  schemaPayoutLogicTable,
  TPayoutLogicTable,
} from '@src/types/exchangePackages.ts';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

interface PayoutLogicDialogProps {
  open: boolean;
  isAdd: boolean;
  onOpenChange: (open: boolean) => void;
  values: TPayoutLogicTable | null;
  onSubmit?: (values: TPayoutLogicTable) => void;
}
export const PayoutLogicDialog: FC<PayoutLogicDialogProps> = ({
  open,
  isAdd,
  values,
  onOpenChange,
  onSubmit,
}) => {
  const { control, handleSubmit, reset } = useForm<TPayoutLogicTable>({
    resolver: yupResolver(schemaPayoutLogicTable),
  });

  useEffect(() => {
    if (open) {
      reset(values || {});
    }
  }, [open, values, reset]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    reset({});
  }, [onOpenChange, reset]);

  const handleSubmitForm = (data: TPayoutLogicTable) => {
    onSubmit?.(data);
    handleClose();
    reset({});
  };

  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80"
          data-slot="sheet-overlay"
        />
        <Dialog.Content className="fixed left-1/2 top-1/2 max-h-[85vh] lg:w-[805px] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-md bg-[#414141] p-11 shadow-[var(--shadow-6)] focus:outline-none data-[state=open]:animate-contentShow z-50">
          <Dialog.Close asChild>
            <Button
              aria-label="Close"
              className="[&_svg]:!size-auto absolute right-[35px] top-[43px] inline-flex size-[25px] appearance-none items-center justify-center rounded-full"
              variant={'ghost'}
              onClick={handleClose}
            >
              <CloseIcon />
            </Button>
          </Dialog.Close>
          <div className="overflow-auto max-h-[calc(85vh-100px)] text-xs">
            <div className={'flex flex-col gap-y-9'}>
              <Dialog.Title
                className={'text-2xl'}
              >{`${isAdd ? 'New' : 'Edit'} Payout Logic`}</Dialog.Title>
              <form
                autoComplete="off"
                className={'flex flex-col gap-y-7.5'}
                onSubmit={handleSubmit(handleSubmitForm)}
              >
                <div className={'flex lg:flex-row flex-col w-full gap-7.5'}>
                  <Controller
                    control={control}
                    name="payoutFromNth"
                    render={({ field }) => (
                      <div className={'flex flex-1 flex-col gap-y-3.5'}>
                        <p>Payout from nth</p>
                        <Input
                          placeholder={'Enter'}
                          textSize={'xs'}
                          type={'number'}
                          variant={'secondary'}
                          {...field}
                        />
                      </div>
                    )}
                  />
                  <Controller
                    control={control}
                    name="payoutToNth"
                    render={({ field }) => (
                      <div className={'flex flex-1 flex-col gap-y-3.5'}>
                        <p>Payout to nth</p>
                        <Input
                          placeholder={'Enter'}
                          textSize={'xs'}
                          type={'number'}
                          variant={'secondary'}
                          {...field}
                        />
                      </div>
                    )}
                  />
                  <Controller
                    control={control}
                    name="tradingDaysRequired"
                    render={({ field }) => (
                      <div className={'flex flex-1 flex-col gap-y-3.5'}>
                        <p>Trading Days Required</p>
                        <Input
                          placeholder={'Enter'}
                          textSize={'xs'}
                          type={'number'}
                          variant={'secondary'}
                          {...field}
                        />
                      </div>
                    )}
                  />
                </div>
                <div className={'flex lg:flex-row flex-col w-full gap-7.5'}>
                  <Controller
                    control={control}
                    name="dailyProfitRequired"
                    render={({ field }) => (
                      <div className={'flex flex-1 flex-col gap-y-3.5'}>
                        <p>Daily Profit Required*</p>
                        <Input
                          placeholder={'Enter'}
                          textSize={'xs'}
                          type={'number'}
                          variant={'secondary'}
                          {...field}
                        />
                      </div>
                    )}
                  />
                  <Controller
                    control={control}
                    name="minPayout"
                    render={({ field }) => (
                      <div className={'flex flex-1 flex-col gap-y-3.5'}>
                        <p>Min Payout $</p>
                        <Input
                          placeholder={'Enter'}
                          textSize={'xs'}
                          type={'number'}
                          variant={'secondary'}
                          {...field}
                        />
                      </div>
                    )}
                  />
                  <Controller
                    control={control}
                    name="maxPayout"
                    render={({ field }) => (
                      <div className={'flex flex-1 flex-col gap-y-3.5'}>
                        <p>Max Payout $</p>
                        <Input
                          placeholder={'Enter'}
                          textSize={'xs'}
                          type={'number'}
                          variant={'secondary'}
                          {...field}
                        />
                      </div>
                    )}
                  />
                </div>
                <div className={'flex lg:flex-row flex-col w-full gap-7.5'}>
                  <Controller
                    control={control}
                    name="requiredBalanceThreshold"
                    render={({ field }) => (
                      <div className={'flex flex-1 flex-col gap-y-3.5'}>
                        <p>Required Balance Threshold $</p>
                        <Input
                          placeholder={'Enter'}
                          textSize={'xs'}
                          type={'number'}
                          variant={'secondary'}
                          {...field}
                        />
                      </div>
                    )}
                  />
                  <Controller
                    control={control}
                    name="clientSplit"
                    render={({ field }) => (
                      <div className={'flex flex-1 flex-col gap-y-3.5'}>
                        <p>{`Client Split % (after initial 100%)`}</p>
                        <Input
                          placeholder={'Enter'}
                          textSize={'xs'}
                          type={'number'}
                          variant={'secondary'}
                          {...field}
                        />
                      </div>
                    )}
                  />
                  <div className={'flex-1'} />
                </div>
                <div className={'flex w-full justify-end'}>
                  <Button size={'sm'} type={'submit'} variant={'lime'}>
                    Save
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
